import React from "react";

/**
 * Render article body with support for YouTube / Twitter (X) / Instagram
 * embeds. Anything on its own line that is a recognized URL becomes an
 * iframe; everything else stays a plain paragraph.
 *
 * No third-party JS is loaded (Twitter/Instagram use the public
 * publish.twitter.com/instagram.com/p/{id}/embed pages), so this is
 * completely free and safe.
 */

// Inline image: ![alt](url) — a whole-line pattern
const IMG_LINE_REGEX = /^\!\[([^\]]*)\]\(([^)]+)\)$/;

// Inline hyperlink & image patterns (used inside paragraphs)
const LINK_INLINE = /\[([^\]]+)\]\(([^)]+)\)/g;
const IMG_INLINE = /!\[([^\]]*)\]\(([^)]+)\)/g;

function InlineImage({ src, alt }) {
  return (
    <figure className="my-6" data-testid="embed-inline-image">
      <img src={src} alt={alt || ""} loading="lazy" className="w-full h-auto border border-[#0F0F0F]/15 bg-[#F4F4F0]" />
      {alt && <figcaption className="mt-2 text-[13px] italic text-[#4A4A4A] text-center">{alt}</figcaption>}
    </figure>
  );
}

/** Convert a paragraph string into React nodes, rendering [text](url) as
 *  hyperlinks. Safe: no dangerouslySetInnerHTML. Any URL is allowed.
 *
 *  We use TWO separate regex passes (image first, then link) so that adding
 *  or reordering a pattern in the future cannot silently break link
 *  rendering via shifted capture-group indices.
 */
function renderInline(text) {
  // First pass: split on inline images (rare inside paragraphs but supported)
  const imgSplit = [];
  let cur = 0;
  const imgRe = new RegExp(IMG_INLINE.source, "g");
  let m;
  while ((m = imgRe.exec(text)) !== null) {
    if (m.index > cur) imgSplit.push({ type: "text", value: text.slice(cur, m.index) });
    imgSplit.push({ type: "img", alt: m[1], src: m[2] });
    cur = m.index + m[0].length;
  }
  if (cur < text.length) imgSplit.push({ type: "text", value: text.slice(cur) });
  if (imgSplit.length === 0) imgSplit.push({ type: "text", value: text });

  const parts = [];
  imgSplit.forEach((chunk, ci) => {
    if (chunk.type === "img") {
      parts.push(<InlineImage key={`img-${ci}`} src={chunk.src} alt={chunk.alt} />);
      return;
    }
    // Second pass on plain-text chunk: [label](url)
    const linkRe = new RegExp(LINK_INLINE.source, "g");
    let last = 0;
    let mm;
    while ((mm = linkRe.exec(chunk.value)) !== null) {
      if (mm.index > last) parts.push(chunk.value.slice(last, mm.index));
      const label = mm[1];
      const href = mm[2];
      const external = /^https?:\/\//i.test(href);
      parts.push(
        <a
          key={`lnk-${ci}-${mm.index}`}
          href={href}
          {...(external ? { target: "_blank", rel: "noreferrer noopener" } : {})}
          className="text-[#D92525] underline underline-offset-2 hover:opacity-80"
        >
          {label}
        </a>,
      );
      last = mm.index + mm[0].length;
    }
    if (last < chunk.value.length) parts.push(chunk.value.slice(last));
  });
  return parts.length ? parts : [text];
}
const YT_REGEX =
  /^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/;

const TWEET_REGEX =
  /^(?:https?:\/\/)?(?:www\.|mobile\.)?(?:twitter\.com|x\.com)\/([A-Za-z0-9_]{1,20})\/status(?:es)?\/(\d{6,25})/;

const INSTA_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]{3,20})/;

const FB_REGEX =
  /^(?:https?:\/\/)?(?:www\.|m\.)?facebook\.com\/[^\s]+/;

// --- Embed builders -------------------------------------------------------
function YouTubeEmbed({ id }) {
  return (
    <div className="my-6 aspect-video w-full border border-[#0F0F0F]/20 rounded-md overflow-hidden bg-black" data-testid="embed-youtube">
      <iframe
        title="YouTube video"
        src={`https://www.youtube.com/embed/${id}`}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        className="w-full h-full"
      />
    </div>
  );
}

function TweetEmbed({ user, id }) {
  return (
    <div className="my-6 border border-[#0F0F0F]/20 rounded-md overflow-hidden" data-testid="embed-tweet">
      <iframe
        title="Tweet"
        src={`https://platform.twitter.com/embed/Tweet.html?id=${id}`}
        loading="lazy"
        allow="clipboard-write"
        className="w-full"
        style={{ height: 620, border: 0 }}
      />
      <div className="text-[11px] text-[#4A4A4A] px-3 py-1.5 border-t border-[#0F0F0F]/10">
        Source: @{user} · <a className="link-underline" href={`https://x.com/${user}/status/${id}`} target="_blank" rel="noreferrer">View on X</a>
      </div>
    </div>
  );
}

function InstaEmbed({ id }) {
  return (
    <div className="my-6 border border-[#0F0F0F]/20 rounded-md overflow-hidden" data-testid="embed-instagram">
      <iframe
        title="Instagram post"
        src={`https://www.instagram.com/p/${id}/embed`}
        loading="lazy"
        scrolling="no"
        allowTransparency="true"
        className="w-full bg-white"
        style={{ height: 680, border: 0 }}
      />
    </div>
  );
}

function FBEmbed({ url }) {
  return (
    <div className="my-6 border border-[#0F0F0F]/20 rounded-md overflow-hidden" data-testid="embed-facebook">
      <iframe
        title="Facebook post"
        src={`https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(url)}&show_text=true&width=500`}
        loading="lazy"
        scrolling="no"
        allowFullScreen
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        className="w-full bg-white"
        style={{ height: 560, border: 0 }}
      />
    </div>
  );
}

// --- Line dispatcher ------------------------------------------------------
function renderLine(line, i) {
  const trimmed = line.trim();
  if (!trimmed) return null;

  // Full-line inline image: ![alt](url)
  const im = trimmed.match(IMG_LINE_REGEX);
  if (im) return <InlineImage key={i} src={im[2]} alt={im[1]} />;

  const yt = trimmed.match(YT_REGEX);
  if (yt) return <YouTubeEmbed key={i} id={yt[1]} />;

  const tw = trimmed.match(TWEET_REGEX);
  if (tw) return <TweetEmbed key={i} user={tw[1]} id={tw[2]} />;

  const ig = trimmed.match(INSTA_REGEX);
  if (ig) return <InstaEmbed key={i} id={ig[1]} />;

  const fb = trimmed.match(FB_REGEX);
  if (fb) return <FBEmbed key={i} url={trimmed} />;

  // Regular paragraph — support inline [text](url) links and ![](url) images
  return <p key={i}>{renderInline(trimmed)}</p>;
}

export default function RichBody({ text = "" }) {
  const lines = text.split(/\n+/);
  return <>{lines.map((l, i) => renderLine(l, i))}</>;
}
